// ===================================================================
// ProfessionalPaymentConfig.tsx - Pagos: cuándo y cómo te pagan
//
// Lavada de cara oct-2026 con el diseño del asistente, Horarios y Servicios
// (MarcaUI / Marca.css). La lógica es la de antes (cargar, validar, guardar,
// vincular Mercado Pago); "Guardar" aparece cuando hay cambios sin guardar,
// como en Horarios.
// ===================================================================

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Banknote,
  CheckCircle,
  AlertCircle,
  Loader2,
  Link2,
  Unlink,
  Percent,
  Wallet,
  X
} from 'lucide-react';
import { PortadaMarca, OpcionConInterruptor } from './MarcaUI';

interface PaymentConfig {
  tipo_cobro: 'solo_sena' | 'consulta_completa' | 'efectivo';
  porcentaje_sena: number;
  acepta_transferencia: boolean;
  acepta_mercadopago: boolean;
  acepta_efectivo: boolean;
  alias_cbu: string;
  titular_cuenta: string;
}

interface MercadoPagoStatus {
  isLinked: boolean;
  mp_user_id: string | null;
  mp_linked_at: string | null;
}

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Rubro del profesional, para hablarle de pacientes o de clientes
const leerTipo = (): 'salud' | 'belleza' => {
  try {
    return JSON.parse(localStorage.getItem('user') || '{}').professional_type === 'belleza' ? 'belleza' : 'salud';
  } catch {
    return 'salud';
  }
};

const ProfessionalPaymentConfig: React.FC = () => {
  const navigate = useNavigate();
  const [config, setConfig] = useState<PaymentConfig>({
    tipo_cobro: 'consulta_completa',
    porcentaje_sena: 30,
    acepta_transferencia: true,
    acepta_mercadopago: false,
    acepta_efectivo: true,
    alias_cbu: '',
    titular_cuenta: ''
  });

  const [mpStatus, setMpStatus] = useState<MercadoPagoStatus>({
    isLinked: false,
    mp_user_id: null,
    mp_linked_at: null
  });

  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [loadingMP, setLoadingMP] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  // Lo último cargado o guardado, para saber si hay cambios sin guardar
  const [configGuardada, setConfigGuardada] = useState('');
  const hayCambios = configGuardada !== '' && JSON.stringify(config) !== configGuardada;

  const tipo = leerTipo();
  const quienes = tipo === 'salud' ? 'tus pacientes' : 'tus clientes';
  const elPaciente = tipo === 'salud' ? 'el paciente' : 'el cliente';

  useEffect(() => {
    loadConfig();
    checkMercadoPagoStatus();
  }, []);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const mpLinked = urlParams.get('mp_linked');

    if (mpLinked === 'success') {
      showToast('¡Mercado Pago vinculado!', 'success');
      checkMercadoPagoStatus();
      loadConfig();
      window.history.replaceState({}, '', window.location.pathname);
    } else if (mpLinked === 'error') {
      showToast('No se pudo vincular Mercado Pago', 'error');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const loadConfig = async () => {
    try {
      setLoadingData(true);
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await axios.get(`${API_URL}/api/payments/config/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        const cargada: PaymentConfig = {
          tipo_cobro: response.data.data.tipo_cobro || 'consulta_completa',
          porcentaje_sena: parseInt(response.data.data.porcentaje_sena) || 30,
          acepta_transferencia: response.data.data.acepta_transferencia ?? true,
          acepta_mercadopago: response.data.data.acepta_mercadopago ?? false,
          acepta_efectivo: response.data.data.acepta_efectivo ?? true,
          alias_cbu: response.data.data.alias_cbu || '',
          titular_cuenta: response.data.data.titular_cuenta || ''
        };
        setConfig(cargada);
        setConfigGuardada(JSON.stringify(cargada));
      }
    } catch (error: any) {
      console.error('Error cargando configuración:', error);
    } finally {
      setLoadingData(false);
    }
  };

  const checkMercadoPagoStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const response = await axios.get(`${API_URL}/api/mercadopago/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setMpStatus({
          isLinked: response.data.isLinked,
          mp_user_id: response.data.mp_user_id,
          mp_linked_at: response.data.mp_linked_at
        });
      }
    } catch (error) {
      console.error('Error verificando Mercado Pago:', error);
    }
  };

  const handleConnectMercadoPago = async () => {
    try {
      setLoadingMP(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/mercadopago/auth/connect`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.authUrl) {
        window.location.href = response.data.authUrl;
      }
    } catch (error: any) {
      showToast('No se pudo conectar con Mercado Pago', 'error');
      setLoadingMP(false);
    }
  };

  const handleDisconnectMercadoPago = async () => {
    if (!window.confirm('¿Seguro que querés desvincular tu cuenta de Mercado Pago?')) return;
    try {
      setLoadingMP(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/api/mercadopago/auth/disconnect`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        showToast('Mercado Pago desvinculado', 'success');
        setMpStatus({ isLinked: false, mp_user_id: null, mp_linked_at: null });
        if (config.acepta_mercadopago) {
          setConfig({ ...config, acepta_mercadopago: false });
        }
      }
    } catch (error: any) {
      showToast('No se pudo desvincular', 'error');
    } finally {
      setLoadingMP(false);
    }
  };

  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const handleSubmit = async () => {
    if (config.tipo_cobro === 'solo_sena' && (config.porcentaje_sena < 10 || config.porcentaje_sena > 100)) {
      showToast('La seña tiene que estar entre 10% y 100%', 'error');
      return;
    }

    if (config.tipo_cobro !== 'efectivo') {
      if (!config.acepta_transferencia && !config.acepta_mercadopago) {
        showToast('Prendé al menos una forma de pago: transferencia o Mercado Pago', 'error');
        return;
      }
      if (config.acepta_transferencia && (!config.alias_cbu.trim() || !config.titular_cuenta.trim())) {
        showToast('Completá el alias o CBU y el titular de la cuenta', 'error');
        return;
      }
      if (config.acepta_mercadopago && !mpStatus.isLinked) {
        showToast('Primero vinculá tu cuenta de Mercado Pago', 'error');
        return;
      }
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(`${API_URL}/api/payments/config`, config, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setConfigGuardada(JSON.stringify(config));
        showToast('Pagos guardados', 'success');
      } else {
        showToast(response.data.error || 'No se pudo guardar', 'error');
      }
    } catch (error: any) {
      showToast(error.response?.data?.error || 'No se pudo guardar la configuración', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Cuando se elige "en el consultorio", se apagan los métodos online
  const handleTipoCobro = (tipoCobro: PaymentConfig['tipo_cobro']) => {
    if (tipoCobro === 'efectivo') {
      setConfig({
        ...config,
        tipo_cobro: tipoCobro,
        acepta_transferencia: false,
        acepta_mercadopago: false,
        acepta_efectivo: true
      });
    } else {
      setConfig({
        ...config,
        tipo_cobro: tipoCobro,
        acepta_efectivo: config.acepta_efectivo
      });
    }
  };

  const descartarCambios = () => {
    if (configGuardada) setConfig(JSON.parse(configGuardada));
  };

  if (loadingData) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-sky-600 mx-auto mb-3" />
          <p className="text-gray-500">Cargando pagos...</p>
        </div>
      </div>
    );
  }

  // "En el consultorio" primero: es con lo que arranca toda cuenta nueva
  const tipoCobroOptions = [
    {
      id: 'efectivo' as const,
      icon: <Banknote className="w-5 h-5" />,
      title: 'En el consultorio',
      desc: `Reservan sin pagar y cobrás cuando llegan.`,
    },
    {
      id: 'solo_sena' as const,
      icon: <Percent className="w-5 h-5" />,
      title: 'Una seña al reservar',
      desc: `${elPaciente.charAt(0).toUpperCase()}${elPaciente.slice(1)} paga una parte y el resto en el consultorio.`,
    },
    {
      id: 'consulta_completa' as const,
      icon: <Wallet className="w-5 h-5" />,
      title: 'Todo al reservar',
      desc: `${elPaciente.charAt(0).toUpperCase()}${elPaciente.slice(1)} paga el total antes del turno.`,
    }
  ];

  const resumen = config.tipo_cobro === 'efectivo'
    ? 'Se paga en el consultorio'
    : config.tipo_cobro === 'solo_sena'
      ? `Seña del ${config.porcentaje_sena}%`
      : 'Todo al reservar';

  const tarjeta = 'bg-white rounded-3xl border border-gray-200 shadow-sm p-4 sm:p-5';
  const campo = 'marca-campo w-full rounded-xl border border-gray-300 bg-white px-3 py-2.5 text-base sm:text-sm text-gray-800';

  return (
    <div className={`marca marca--${tipo} min-h-screen bg-slate-50 text-left`}>
      <PortadaMarca
        titulo="Pagos"
        bajada={`Cuándo y cómo te pagan ${quienes}.`}
        onVolver={() => navigate(-1)}
      >
        <div className="flex flex-wrap gap-2 text-xs font-semibold">
          <span className="rounded-full bg-white/20 px-3 py-1">{resumen}</span>
          {mpStatus.isLinked && <span className="rounded-full bg-white/20 px-3 py-1">Mercado Pago vinculado</span>}
        </div>
      </PortadaMarca>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 -mt-6 relative pb-40 space-y-4">
        {/* ==================== Cuándo te pagan ==================== */}
        <section className={`${tarjeta} animate-entrada-abajo`}>
          <h2 className="text-base font-bold text-gray-900">¿Cuándo te pagan?</h2>
          <p className="text-sm text-gray-500 mt-0.5 mb-4">Lo que ve {elPaciente} al reservar desde tu link.</p>

          <div className="space-y-2.5" role="radiogroup" aria-label="Cuándo te pagan">
            {tipoCobroOptions.map((option) => {
              const elegido = config.tipo_cobro === option.id;
              return (
                <button
                  key={option.id}
                  type="button"
                  role="radio"
                  aria-checked={elegido}
                  onClick={() => handleTipoCobro(option.id)}
                  className={`w-full flex items-center gap-3 rounded-2xl border p-3.5 text-left transition-colors ${
                    elegido ? 'marca-tarjeta' : 'bg-white border-gray-200 hover:bg-gray-50'
                  }`}
                  style={elegido ? { borderColor: 'var(--a2)' } : undefined}
                >
                  <span className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${elegido ? 'marca-boton' : 'bg-gray-100 text-gray-500'}`}>
                    {option.icon}
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className={`block font-semibold ${elegido ? 'marca-acento' : 'text-gray-900'}`}>{option.title}</span>
                    <span className="block text-xs text-gray-500 mt-0.5">{option.desc}</span>
                  </span>
                  <span
                    className="w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0"
                    style={{ borderColor: elegido ? 'var(--a2)' : '#d1d5db' }}
                  >
                    {elegido && <span className="w-2.5 h-2.5 rounded-full" style={{ background: 'var(--a2)' }} />}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Porcentaje de seña */}
          {config.tipo_cobro === 'solo_sena' && (
            <div className="mt-4 rounded-2xl border border-gray-200 p-4 animate-panel-in">
              <div className="flex items-center justify-between gap-3">
                <label htmlFor="pagos-sena" className="text-sm font-semibold text-gray-800">Porcentaje de la seña</label>
                <span className="marca-acento text-2xl font-bold">{config.porcentaje_sena}%</span>
              </div>
              <input
                id="pagos-sena"
                type="range"
                min="10"
                max="100"
                step="5"
                value={config.porcentaje_sena}
                onChange={(e) => setConfig({ ...config, porcentaje_sena: parseInt(e.target.value) })}
                className="w-full mt-3"
                style={{ accentColor: 'var(--a2)' }}
              />
              <p className="mt-1 text-xs text-gray-500">
                Ejemplo: en una consulta de $20.000, la seña es de ${(20000 * config.porcentaje_sena / 100).toLocaleString('es-AR')}.
              </p>
            </div>
          )}

          {config.tipo_cobro === 'efectivo' && (
            <p className="mt-4 text-xs text-gray-500 leading-snug">
              El turno queda reservado sin pago previo. Si más adelante querés pedir seña, la activás acá mismo.
            </p>
          )}
        </section>

        {/* ==================== Cómo te pagan (si se cobra al reservar) ==================== */}
        {config.tipo_cobro !== 'efectivo' && (
          <section className={`${tarjeta} space-y-5 animate-entrada-abajo`} style={{ animationDelay: '60ms' }}>
            <div>
              <h2 className="text-base font-bold text-gray-900">¿Cómo te pagan?</h2>
              <p className="text-sm text-gray-500 mt-0.5">Prendé al menos una forma de pago.</p>
            </div>

            <OpcionConInterruptor
              titulo="Transferencia"
              detalle={`Sin comisiones: ${elPaciente} transfiere y sube el comprobante.`}
              activo={config.acepta_transferencia}
              onCambio={(v) => setConfig({ ...config, acepta_transferencia: v })}
            >
              <div className="space-y-3">
                <div>
                  <label htmlFor="pagos-alias" className="block text-xs font-semibold text-gray-600 mb-1.5">Alias o CBU</label>
                  <input
                    id="pagos-alias"
                    type="text"
                    placeholder="Ej: MEDICO.GOMEZ o tu CBU completo"
                    value={config.alias_cbu}
                    onChange={(e) => setConfig({ ...config, alias_cbu: e.target.value })}
                    className={campo}
                  />
                </div>
                <div>
                  <label htmlFor="pagos-titular" className="block text-xs font-semibold text-gray-600 mb-1.5">Titular de la cuenta</label>
                  <input
                    id="pagos-titular"
                    type="text"
                    placeholder="Ej: Juan Pérez"
                    value={config.titular_cuenta}
                    onChange={(e) => setConfig({ ...config, titular_cuenta: e.target.value })}
                    className={campo}
                  />
                </div>
              </div>
            </OpcionConInterruptor>

            <div className="border-t border-gray-100 pt-5">
              {mpStatus.isLinked ? (
                <OpcionConInterruptor
                  titulo="Mercado Pago"
                  detalle="Tarjeta o dinero en cuenta, se confirma al instante (comisión de Mercado Pago, ~6%)."
                  activo={config.acepta_mercadopago}
                  onCambio={(v) => setConfig({ ...config, acepta_mercadopago: v })}
                >
                  <div className="flex items-center justify-between gap-3 rounded-xl border border-gray-200 px-3 py-2.5 text-sm">
                    <span className="flex items-center gap-2 text-gray-700">
                      <CheckCircle className="w-4 h-4 text-green-600" /> Cuenta vinculada
                      {mpStatus.mp_user_id && <span className="text-gray-400">(ID {mpStatus.mp_user_id})</span>}
                    </span>
                    <button
                      type="button"
                      onClick={handleDisconnectMercadoPago}
                      disabled={loadingMP}
                      className="text-xs font-semibold text-red-500 hover:text-red-700 inline-flex items-center gap-1"
                    >
                      <Unlink className="w-3 h-3" /> Desvincular
                    </button>
                  </div>
                </OpcionConInterruptor>
              ) : (
                <div>
                  <p className="text-sm font-semibold text-gray-900">Mercado Pago</p>
                  <p className="text-xs text-gray-500 mt-0.5 leading-snug">
                    Tarjeta o dinero en cuenta, se confirma al instante (comisión de Mercado Pago, ~6%). Para usarlo, vinculá tu cuenta.
                  </p>
                  <button
                    type="button"
                    onClick={handleConnectMercadoPago}
                    disabled={loadingMP}
                    className="mt-3 inline-flex items-center gap-2 rounded-xl border marca-tarjeta marca-acento px-4 py-2 text-sm font-semibold"
                  >
                    {loadingMP ? <><Loader2 className="w-4 h-4 animate-spin" /> Conectando...</> : <><Link2 className="w-4 h-4" /> Vincular Mercado Pago</>}
                  </button>
                </div>
              )}
            </div>

            <div className="border-t border-gray-100 pt-5">
              <OpcionConInterruptor
                titulo="También en efectivo"
                detalle={`${elPaciente.charAt(0).toUpperCase()}${elPaciente.slice(1)} puede elegir pagar en el consultorio.`}
                activo={config.acepta_efectivo}
                onCambio={(v) => setConfig({ ...config, acepta_efectivo: v })}
              />
            </div>
          </section>
        )}

        {/* Mercado Pago vinculado pero sin usar (cobro en el consultorio) */}
        {config.tipo_cobro === 'efectivo' && mpStatus.isLinked && (
          <section className={`${tarjeta} flex items-center justify-between gap-3`}>
            <span className="text-sm text-gray-700">Mercado Pago vinculado (no se usa mientras cobrás en el consultorio)</span>
            <button
              type="button"
              onClick={handleDisconnectMercadoPago}
              disabled={loadingMP}
              className="text-xs font-semibold text-red-500 hover:text-red-700 shrink-0"
            >
              Desvincular
            </button>
          </section>
        )}
      </div>

      {/* Avisos: arriba, debajo del encabezado del sitio */}
      {toast && (
        <div className="fixed inset-x-0 top-20 z-[60] px-4 pointer-events-none">
          <div
            className={`max-w-md mx-auto pointer-events-auto flex items-start gap-2 rounded-2xl border px-4 py-3 text-sm shadow-lg animate-slide-up ${
              toast.type === 'error' ? 'bg-red-50 border-red-200 text-red-800' : 'bg-white border-gray-200 text-gray-800'
            }`}
            role={toast.type === 'error' ? 'alert' : 'status'}
          >
            {toast.type === 'error' ? <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /> : <CheckCircle className="w-4 h-4 mt-0.5 shrink-0 text-green-600" />}
            <span className="flex-1">{toast.message}</span>
            <button type="button" onClick={() => setToast(null)} className="opacity-60 hover:opacity-100" aria-label="Cerrar aviso">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Guardar: aparece cuando hay cambios. En el celular, arriba de la
          barra de abajo (fab-sobre-barra). */}
      {(hayCambios || loading) && (
        <div className="fab-sobre-barra fixed inset-x-0 bottom-4 sm:bottom-6 z-40 px-4 pointer-events-none">
          <div className="max-w-3xl mx-auto pointer-events-auto flex items-center gap-2 rounded-2xl bg-white border border-gray-200 shadow-xl px-3 py-2.5 animate-slide-up">
            <span className="flex-1 text-sm font-medium text-gray-700 pl-1">Tenés cambios sin guardar</span>
            <button
              type="button"
              onClick={descartarCambios}
              disabled={loading}
              className="px-3 py-2 rounded-xl text-sm font-semibold text-gray-500 hover:bg-gray-100"
            >
              Descartar
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={loading}
              className="marca-boton px-4 py-2 rounded-xl text-sm font-semibold flex items-center gap-2"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              {loading ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfessionalPaymentConfig;
